import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { TooltipRenderProps } from 'react-joyride';
import type { TourStepDef } from './welcomeTourSteps';

interface DotsProgressProps {
  readonly index: number;
  readonly size: number;
}

// Dots stepper: un punto por paso, el actual resaltado.
function DotsProgress({ index, size }: DotsProgressProps) {
  return (
    <div className="flex items-center gap-1.5" aria-label={`Paso ${index + 1} de ${size}`}>
      {Array.from({ length: size }, (_, i) => (
        <span
          key={i}
          className={`h-1.5 rounded-full motion-safe:transition-all motion-safe:duration-200 ${
            i === index ? 'w-4 bg-blue-600' : 'w-1.5 bg-slate-300'
          }`}
        />
      ))}
    </div>
  );
}

export function TourTooltip(props: TooltipRenderProps) {
  const { index, size, step, primaryProps, skipProps, backProps, closeProps, tooltipProps } = props;
  // El contenido español y las etiquetas viven en step.data (inyectado en WelcomeTour.tsx).
  const def = step.data as TourStepDef | undefined;
  const esAccionable = def?.tipo === 'accionable';
  const esLlegada = def?.tipo === 'llegada';

  // Lo que más llama la atención al entrar a una sección es su propio título, arriba a
  // la izquierda del contenido — por eso la tarjeta se ancla cerca de ahí (no a una
  // esquina fija de la pantalla, donde el usuario podía perderla de vista). Se deja un
  // margen vertical (no pegada al borde superior) para despejar el título/subtítulo de
  // la sección, que varía un poco de una página a otra.
  const [posicion, setPosicion] = useState<{ top: number; left: number } | null>(null);
  const envoltorioRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!esLlegada) return;
    const MARGEN = 16;
    const medir = () => {
      const contenedor = document.querySelector<HTMLElement>('[data-tour="contenido-principal"]');
      if (!contenedor) return;
      const rect = contenedor.getBoundingClientRect();
      // `rect.top` es relativo a la ventana y la tarjeta se dibuja `fixed`, así que la
      // medida solo sirve mientras el título de la sección esté a la vista. Con la pantalla
      // desplazada hacia abajo el valor es negativo y la tarjeta se dibujaba fuera de la
      // ventana, sin forma de tocar "Continuar": en un teléfono de 375px el recorrido moría
      // en el sexto paso, porque para llegar a la ficha de Aportes hay que bajar la
      // pantalla y esa posición se arrastra a la pantalla siguiente. Se acota a la ventana.
      const alto = envoltorioRef.current?.offsetHeight ?? 200;
      const topMaximo = Math.max(MARGEN, window.innerHeight - alto - MARGEN);
      setPosicion({
        top: Math.min(Math.max(rect.top + 112, MARGEN), topMaximo),
        left: Math.max(rect.left + 16, MARGEN),
      });
    };
    medir();
    window.addEventListener('resize', medir);
    // Sin escuchar el desplazamiento, la primera medición equivocada quedaba congelada y ni
    // siquiera volver arriba rescataba la tarjeta — verificado: subir la pantalla a mano
    // dejaba el globo en la misma posición inalcanzable.
    window.addEventListener('scroll', medir, { passive: true });
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir);
    };
  }, [esLlegada, step.title]);

  const tarjeta = (
    <div
      {...tooltipProps}
      data-testid="tour-tooltip"
      className={`relative w-[320px] max-w-[calc(100vw-2rem)] rounded-xl border border-slate-100 shadow-lg shadow-slate-200/60 p-5 ${
        esLlegada ? 'bg-white/75 backdrop-blur-sm' : 'bg-white'
      }`}
    >
      <button
        {...closeProps}
        className="absolute top-3 right-3 p-1 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"
        aria-label="Cerrar tour"
        title="Cerrar tour"
      >
        <X className="w-4 h-4" />
      </button>

      {step.title && (
        <h3 className="text-base font-semibold text-slate-800 pr-6">{step.title}</h3>
      )}
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{step.content}</p>

      {/* Dots en su propia fila: con 15 pasos + hasta 3 botones, compartir una sola fila
          desbordaba el ancho del tooltip en móvil (el botón "Continuar" terminaba
          renderizado fuera del viewport — bug real encontrado con Playwright). */}
      <div className="mt-4 flex justify-center">
        <DotsProgress index={index} size={size} />
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        {index > 0 && !esLlegada && (
          <button
            {...backProps}
            aria-label="Atrás"
            title="Atrás"
            className="px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
          >
            Atrás
          </button>
        )}
        <button
          {...skipProps}
          aria-label={def?.skipLabel ?? 'Salir'}
          title={def?.skipLabel ?? 'Salir'}
          className="px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
        >
          {def?.skipLabel ?? 'Salir'}
        </button>
        {!esAccionable && (
          <button
            {...primaryProps}
            aria-label={def?.primaryLabel ?? 'Continuar'}
            title={def?.primaryLabel ?? 'Continuar'}
            className="px-4 py-1.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {def?.primaryLabel ?? 'Continuar'}
          </button>
        )}
      </div>
    </div>
  );

  // Checkpoint de llegada: el target (contenido principal) ocupa casi toda la
  // pantalla, así que la posición que floating-ui calcula para ese target no sirve
  // de nada — Joyride la aplica en un wrapper que no controlamos, y ese wrapper puede
  // traer su propio `transform` (rompería un `position: fixed` anidado). Se porta la
  // tarjeta directo a `document.body`, medida contra el contenedor real, fuera del
  // wrapper. Mientras no hay medición (primer frame) queda oculta para no parpadear
  // en la esquina superior izquierda de la ventana.
  if (esLlegada && typeof document !== 'undefined') {
    return createPortal(
      <div
        ref={envoltorioRef}
        className="fixed z-[110]"
        style={posicion ? { top: posicion.top, left: posicion.left } : { top: 0, left: 0, visibility: 'hidden' }}
      >
        {tarjeta}
      </div>,
      document.body,
    );
  }

  return tarjeta;
}
